import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { validators } from 'investira.sdk';
import {
  Stack,
  Button,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  ToggleButton,
  ToggleButtonGroup
} from 'investira.react.components';

export const METRICAS_TIPO = {
  CS: { id: 'cs', descricao: 'Capital Social' },
  PL: { id: 'pl', descricao: 'Patrimônio Líquido' },
  MARKET_VALUE: { id: 'market_value', descricao: 'Valor de Mercado' }
};

const FilterMetricasEmpresa = memo(props => {
  const { values, onValuesChange, showFaixa = true, showSimilaridade = true } = props;
  const [anchorElMetrica, setAnchorElMetrica] = useState(null);

  const openMetrica = Boolean(anchorElMetrica);

  const handleFilterChange = (pAttribute, pValue) => {
    if (pValue !== null) onValuesChange({ ...values, [pAttribute]: pValue });
  };

  function handleClickMetrica(pEvent) {
    setAnchorElMetrica(pEvent.currentTarget);
  }

  function handleCloseMetrica() {
    setAnchorElMetrica(null);
  }

  function handleSetMetrica(pValue) {
    handleFilterChange('metrica', pValue);
    handleCloseMetrica();
  }

  function renderMetricaMenu(pMetricasTipo, pCurrentMetrica) {
    if (validators.isEmpty(pMetricasTipo)) {
      return null;
    }

    const xOptions = Object.values(pMetricasTipo);

    const xMenu = xOptions.map(xItem => {
      return {
        label: xItem.descricao,
        value: xItem.id
      };
    });

    const xCurrentLabel = xOptions.find(xItem => xItem.id === pCurrentMetrica);

    const xProps = {
      ...(xOptions.length >= 2 ? { onClick: handleClickMetrica } : { disabled: true })
    };

    return (
      <>
        <Button
          variant="outlined"
          size="small"
          disableElevation
          {...xProps}
          sx={{ fontSize: '0.7rem', padding: '3px 8px' }}>
          {xCurrentLabel?.descricao}
        </Button>
        <Menu anchorEl={anchorElMetrica} open={openMetrica} onClose={handleCloseMetrica}>
          {xMenu.map(xItem => {
            return (
              <MenuItem key={xItem.value} onClick={() => handleSetMetrica(xItem.value)}>
                {xItem.label}
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  /**
   * Renderiza o seletor de faixa (Menores/Ambos/Maiores) para filtrar os resultados.
   *
   * @param {"menores"|"proximos"|"maiores"|null|undefined} pCurrentFaixa
   * Valor atualmente selecionado no filtro de faixa.
   *
   * @returns {JSX.Element}
   * Grupo de botões exclusivo (`exclusive`) que emite alterações via `handleFilterChange("faixa", pNewValue)`.
   * Observação: em modo exclusivo, o `pNewValue` pode ser `null` ao desselecionar; nesse caso o componente ignora a mudança.
   */
  function renderFaixaToggle(pCurrentFaixa) {
    return (
      <ToggleButtonGroup
        value={pCurrentFaixa}
        exclusive
        onChange={(e, pNewValue) => handleFilterChange('faixa', pNewValue)}
        size="small"
        color="primary"
        aria-label="Faixa de valores">
        <ToggleButton value="menores" sx={{ fontSize: '0.7rem', padding: '3px 8px' }}>
          Menores
        </ToggleButton>
        <ToggleButton value="proximos" sx={{ fontSize: '0.7rem', padding: '3px 8px' }}>
          Ambos
        </ToggleButton>
        <ToggleButton value="maiores" sx={{ fontSize: '0.7rem', padding: '3px 8px' }}>
          Maiores
        </ToggleButton>
      </ToggleButtonGroup>
    );
  }

  return (
    <Stack spacing={1}>
      <Stack gap={2} direction="row" flexWrap="wrap" alignItems="center">
        {renderMetricaMenu(METRICAS_TIPO, values?.metrica)}
        {showFaixa && renderFaixaToggle(values?.faixa)}
      </Stack>
      {showSimilaridade && (
        <FormControlLabel
          control={
            <Switch
              checked={values?.similaridade === 'setor'}
              onChange={e =>
                handleFilterChange('similaridade', e.target.checked ? 'setor' : 'metrica')
              }
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="textPrimary">
              Mesmo setor
            </Typography>
          }
        />
      )}
    </Stack>
  );
});

FilterMetricasEmpresa.propTypes = {
  values: PropTypes.object,
  onValuesChange: PropTypes.func,
  showFaixa: PropTypes.bool,
  showSimilaridade: PropTypes.bool
};

export default FilterMetricasEmpresa;
